import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, IdCard, Map as MapIcon, PackageCheck, ScanBarcode, Scissors, Truck } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { StatutBadge, fmtDateHeure, nomChauffeur } from "@/components/scans/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { peut } from "@/lib/permissions";

function Tile({ label, value, sub, icon: Icon, tone = "", to, testId, full = false }) {
  const body = (
    <div className={`rounded-xl border border-border bg-card p-4 ${full ? "h-full" : ""} transition-colors ${to ? "hover:border-primary/50" : ""}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">{label}</p>
        {Icon && <Icon size={16} className={tone || "text-muted-foreground"} />}
      </div>
      <p className={`font-heading text-3xl font-bold mt-1 ${tone}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
  return to ? <Link to={to} className={`block ${full ? "h-full" : ""}`}>{body}</Link> : body;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  const heure = new Date().getHours();
  const salut = heure < 18 ? "Bonjour" : "Bonsoir";

  return (
    <AppShell title="Tableau de bord">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold tracking-tight">{salut} {user?.prenom}</h1>
        <p className="text-sm text-muted-foreground">
          {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>

      {!d ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <div className="space-y-6">
          <section>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Scans</p>
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
              <Tile label="Disponibles au dépôt" value={d.scans.disponible} icon={ScanBarcode} tone="text-emerald-600 dark:text-emerald-400" to="/scans" testId="tile-disponibles" full
                sub={`${d.scans.preattribuee} pré-attribuée(s)`} />
              <Tile label="Chez les chauffeurs" value={d.scans.attribuee} icon={Truck} tone="text-primary" to="/scans/reception" testId="tile-attribuees" full
                sub={`${d.remises_jour} remise(s) aujourd'hui`} />
              <Tile label="En attente de constatation" value={d.scans.attente_constatation} icon={AlertTriangle}
                tone={d.scans.attente_constatation ? "text-red-600 dark:text-red-400" : ""} to="/scans/reception" testId="tile-constatation" full
                sub={`${d.scans.avec_defauts} scan(s) avec défauts`} />
              <Tile label="Réceptionnées aujourd'hui" value={d.receptions_jour} icon={PackageCheck} to="/scans/reception" testId="tile-receptions" full
                sub={`${d.scans.total} scan(s) au total · ${d.scans.inactive} inactive(s)`} />
            </div>
          </section>

          <div className="grid gap-6 lg:grid-cols-3">
            <section className="lg:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Scans à réceptionner</p>
                <Link to="/scans/reception" className="text-xs text-primary inline-flex items-center gap-1 hover:underline">Tout voir <ArrowRight size={12} /></Link>
              </div>
              <div className="rounded-xl border border-border bg-card divide-y divide-border">
                {d.a_receptionner.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground flex items-center gap-2"><CheckCircle2 size={15} className="text-emerald-500" /> Toutes les scans sont rentrées.</p>
                ) : d.a_receptionner.map((a) => (
                  <div key={a.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                    <span className="font-mono font-bold w-12">{a.numero}</span>
                    <span className="flex-1 min-w-0 truncate">{nomChauffeur(a.chauffeur)}{a.tournee && <span className="text-muted-foreground"> · T. {a.tournee}</span>}</span>
                    <span className="text-xs text-muted-foreground hidden sm:block">{fmtDateHeure(a.signe_le)}</span>
                    <StatutBadge statut={a.statut} label={a.statut_label} />
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-3">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Tournées & chauffeurs</p>
              <Tile label="Chauffeurs" value={d.chauffeurs.total} icon={IdCard} to="/chauffeurs" sub={`${d.chauffeurs.principaux} principal(aux) · ${d.tournees.total} tournées`} />
              <Link to="/chauffeurs" className="block rounded-xl border border-border bg-card p-4 hover:border-primary/50">
                <p className="text-xs text-muted-foreground">Tournées sans chauffeur principal</p>
                {d.tournees.sans_principal.length === 0
                  ? <p className="text-sm mt-1 flex items-center gap-1.5"><CheckCircle2 size={14} className="text-emerald-500" /> Toutes couvertes</p>
                  : <p className="font-mono text-sm mt-1 leading-relaxed" data-testid="sans-principal">{d.tournees.sans_principal.join(" · ")}</p>}
              </Link>
              {d.predecoupage.total > 0 && peut(user, "carte_tournees_voir") && (
                <Link to="/carte/tournees" className="block rounded-xl border border-border bg-card p-4 hover:border-primary/50">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Scissors size={13} /> Pré-découpage des quartiers</p>
                  <p className="text-sm mt-1">{d.predecoupage.affectees} / {d.predecoupage.total} zones affectées</p>
                  <div className="h-2 rounded-full bg-secondary mt-2 overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${Math.round((d.predecoupage.affectees / d.predecoupage.total) * 100)}%` }} />
                  </div>
                </Link>
              )}
              <Link to="/carte" className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 hover:border-primary/50 text-sm font-medium">
                <MapIcon size={16} className="text-primary" /> Ouvrir la carte des tournées <ArrowRight size={14} className="ml-auto" />
              </Link>
            </section>
          </div>
        </div>
      )}
    </AppShell>
  );
}
